import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["portalId", "appId", "appName", "oAuthScope", "oAuthScopes", "mcpParams", "accountId", "oAuthorMigrationParams", "onComplete", "onCancel", "config", "onClick"];
import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { UnifiedAccountModal as CoreUnifiedAccountModal } from '../account/UnifiedAccountModal';
import { deleteAccount as deleteAccountApi, uninstallApp as uninstallAppApi } from '../account/api/UnifiedAccountApi';
import { InitWrapper } from './components/InitWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function buildUnifiedAccount(options) {
  const {
    displayName,
    apiHandlers,
    defaultConfig,
    defaultProps,
    onInit,
    onConnectComplete,
    onReconnectComplete,
    onDeleteComplete,
    onUninstallComplete
  } = options;
  const UnifiedAccountModalInternal = ({
    inline = false,
    portalId: propPortalId,
    appId: propAppId,
    appName: propAppName,
    oAuthScope: propOAuthScope,
    oAuthScopes: propOAuthScopes,
    mcpParams: propMcpParams,
    accountId,
    oAuthorMigrationParams,
    onComplete,
    onCancel,
    config
  }) => {
    const portalId = propPortalId !== null && propPortalId !== void 0 ? propPortalId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.portalId;
    const appId = propAppId !== null && propAppId !== void 0 ? propAppId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appId;
    const mcpParams = propMcpParams !== null && propMcpParams !== void 0 ? propMcpParams : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.mcpParams;
    const mergedConfig = Object.assign({}, defaultConfig, config);
    const {
      shouldDeleteOnFailure,
      introScreen,
      successScreen
    } = mergedConfig;
    const isReconnect = !!oAuthorMigrationParams || accountId !== undefined;
    const handleComplete = useCallback((newAccountId, resolvedAppId, resolvedPortalId) => {
      const finalAppId = resolvedAppId !== null && resolvedAppId !== void 0 ? resolvedAppId : appId;
      const finalPortalId = resolvedPortalId !== null && resolvedPortalId !== void 0 ? resolvedPortalId : portalId;
      const hook = isReconnect ? onReconnectComplete : onConnectComplete;
      if (hook && newAccountId !== undefined && finalAppId !== undefined && finalPortalId !== undefined) {
        void (async () => {
          try {
            await hook(newAccountId, {
              appId: finalAppId,
              portalId: finalPortalId
            });
            onComplete({
              accountId: newAccountId
            });
          } catch (error) {
            const shouldCleanup = shouldDeleteOnFailure ? shouldDeleteOnFailure(newAccountId) : true;
            if (shouldCleanup) {
              var _apiHandlers$deleteAc;
              const deleteFn = (_apiHandlers$deleteAc = apiHandlers === null || apiHandlers === void 0 ? void 0 : apiHandlers.deleteAccount) !== null && _apiHandlers$deleteAc !== void 0 ? _apiHandlers$deleteAc : deleteAccountApi;
              try {
                await deleteFn(newAccountId);
              } catch (cleanupError) {
                console.error('Failed to clean up account after hook error:', cleanupError);
              }
            }
            throw error;
          }
        })();
      } else {
        try {
          onComplete({
            accountId: newAccountId
          });
        } catch (error) {
          if (newAccountId !== undefined) {
            const shouldCleanup = shouldDeleteOnFailure ? shouldDeleteOnFailure(newAccountId) : true;
            if (shouldCleanup) {
              var _apiHandlers$deleteAc2;
              const deleteFn = (_apiHandlers$deleteAc2 = apiHandlers === null || apiHandlers === void 0 ? void 0 : apiHandlers.deleteAccount) !== null && _apiHandlers$deleteAc2 !== void 0 ? _apiHandlers$deleteAc2 : deleteAccountApi;
              void deleteFn(newAccountId);
            }
          }
          throw error;
        }
      }
    }, [onComplete, shouldDeleteOnFailure, appId, portalId, isReconnect]);
    if (portalId === undefined || appId === undefined) {
      return /*#__PURE__*/_jsx(Alert, {
        type: "danger",
        titleText: "Configuration Error",
        children: /*#__PURE__*/_jsx(View, {
          paddingBlock: "200",
          paddingInline: "200",
          children: /*#__PURE__*/_jsx(Text, {
            children: "portalId and appId are required"
          })
        })
      });
    }
    const renderModal = resolvedProps => {
      var _ref, _ref2, _ref3, _ref4, _ref5, _ref6;
      const appName = (_ref = (_ref2 = propAppName !== null && propAppName !== void 0 ? propAppName : resolvedProps.appName) !== null && _ref2 !== void 0 ? _ref2 : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appName) !== null && _ref !== void 0 ? _ref : '';
      const oAuthScopes = (_ref3 = (_ref4 = (_ref5 = (_ref6 = propOAuthScopes !== null && propOAuthScopes !== void 0 ? propOAuthScopes : propOAuthScope ? propOAuthScope.split(' ').filter(Boolean) : undefined) !== null && _ref6 !== void 0 ? _ref6 : resolvedProps.oAuthScopes) !== null && _ref5 !== void 0 ? _ref5 : resolvedProps.oAuthScope ? resolvedProps.oAuthScope.split(' ').filter(Boolean) : undefined) !== null && _ref4 !== void 0 ? _ref4 : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.oAuthScopes) !== null && _ref3 !== void 0 ? _ref3 : defaultProps !== null && defaultProps !== void 0 && defaultProps.oAuthScope ? defaultProps.oAuthScope.split(' ').filter(Boolean) : undefined;
      return /*#__PURE__*/_jsx(CoreUnifiedAccountModal, {
        inline: inline,
        portalId: portalId,
        appId: appId,
        appName: appName,
        oAuthScopes: oAuthScopes,
        mcpParams: mcpParams,
        accountId: accountId,
        oAuthorMigrationParams: oAuthorMigrationParams,
        onComplete: newAccountId => handleComplete(newAccountId, appId, portalId),
        onCancel: onCancel,
        config: {
          shouldDeleteOnFailure,
          introScreen,
          successScreen
        }
      });
    };
    if (onInit) {
      return /*#__PURE__*/_jsx(InitWrapper, {
        appId: appId,
        portalId: portalId,
        onInit: onInit,
        children: renderModal
      });
    }
    return renderModal({});
  };
  UnifiedAccountModalInternal.displayName = `${displayName}Modal`;
  function UnifiedAccountButtonInternalFn(_ref7) {
    var _ref8, _ref9;
    let {
        portalId: propPortalId,
        appId: propAppId,
        appName: propAppName,
        oAuthScope: propOAuthScope,
        oAuthScopes: propOAuthScopes,
        mcpParams: propMcpParams,
        accountId,
        oAuthorMigrationParams,
        onComplete,
        onCancel = () => {},
        config,
        onClick
      } = _ref7,
      buttonProps = _objectWithoutPropertiesLoose(_ref7, _excluded);
    const [showAccountModal, setShowAccountModal] = useState(false);
    const mergedConfig = Object.assign({}, defaultConfig, config);
    const portalId = propPortalId !== null && propPortalId !== void 0 ? propPortalId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.portalId;
    const appId = propAppId !== null && propAppId !== void 0 ? propAppId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appId;
    const appName = propAppName !== null && propAppName !== void 0 ? propAppName : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appName;
    const oAuthScopes = (_ref8 = (_ref9 = propOAuthScopes !== null && propOAuthScopes !== void 0 ? propOAuthScopes : propOAuthScope ? propOAuthScope.split(' ').filter(Boolean) : undefined) !== null && _ref9 !== void 0 ? _ref9 : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.oAuthScopes) !== null && _ref8 !== void 0 ? _ref8 : defaultProps !== null && defaultProps !== void 0 && defaultProps.oAuthScope ? defaultProps.oAuthScope.split(' ').filter(Boolean) : undefined;
    const mcpParams = propMcpParams !== null && propMcpParams !== void 0 ? propMcpParams : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.mcpParams;
    const closeModal = () => setShowAccountModal(false);
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Button, Object.assign({}, buttonProps, {
        onClick: event => {
          setShowAccountModal(true);
          onClick === null || onClick === void 0 || onClick(event);
        }
      })), showAccountModal && /*#__PURE__*/_jsx(UnifiedAccountModalInternal, {
        portalId: portalId,
        appId: appId,
        appName: appName,
        oAuthScopes: oAuthScopes,
        mcpParams: mcpParams,
        accountId: accountId,
        oAuthorMigrationParams: oAuthorMigrationParams,
        config: mergedConfig,
        onComplete: response => {
          onComplete(response);
          if (!mergedConfig.successScreen) {
            closeModal();
          }
        },
        onCancel: () => {
          closeModal();
          onCancel();
        }
      })]
    });
  }
  UnifiedAccountButtonInternalFn.displayName = `${displayName}Button`;
  const UnifiedAccountButtonInternal = UnifiedAccountButtonInternalFn;
  function useUnifiedAccountModal(modalOptions, callbacks) {
    var _ref10, _ref11;
    const {
      portalId: propPortalId,
      appId: propAppId,
      appName: propAppName,
      oAuthScope: propOAuthScope,
      oAuthScopes: propOAuthScopes,
      mcpParams: propMcpParams,
      accountId,
      oAuthorMigrationParams,
      config,
      usePortal = true
    } = modalOptions;
    const portalId = propPortalId !== null && propPortalId !== void 0 ? propPortalId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.portalId;
    const appId = propAppId !== null && propAppId !== void 0 ? propAppId : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appId;
    const appName = propAppName !== null && propAppName !== void 0 ? propAppName : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.appName;
    const oAuthScopes = (_ref10 = (_ref11 = propOAuthScopes !== null && propOAuthScopes !== void 0 ? propOAuthScopes : propOAuthScope ? propOAuthScope.split(' ').filter(Boolean) : undefined) !== null && _ref11 !== void 0 ? _ref11 : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.oAuthScopes) !== null && _ref10 !== void 0 ? _ref10 : defaultProps !== null && defaultProps !== void 0 && defaultProps.oAuthScope ? defaultProps.oAuthScope.split(' ').filter(Boolean) : undefined;
    const mcpParams = propMcpParams !== null && propMcpParams !== void 0 ? propMcpParams : defaultProps === null || defaultProps === void 0 ? void 0 : defaultProps.mcpParams;
    const {
      onComplete,
      onCancel
    } = callbacks;
    const mergedConfig = Object.assign({}, defaultConfig, config);
    const {
      shouldDeleteOnFailure,
      introScreen,
      successScreen
    } = mergedConfig;
    const isReconnect = !!oAuthorMigrationParams || accountId !== undefined;
    const [isOpen, setIsOpen] = useState(false);
    const [initState, setInitState] = useState(onInit ? {
      status: 'idle'
    } : {
      status: 'ready',
      resolvedProps: {}
    });
    const portalContainerRef = useRef(null);
    useEffect(() => {
      if (!usePortal) return;
      const container = document.createElement('div');
      container.setAttribute('data-unified-account-modal-portal', 'true');
      document.body.appendChild(container);
      portalContainerRef.current = container;
      return () => {
        if (portalContainerRef.current && document.body.contains(portalContainerRef.current)) {
          document.body.removeChild(portalContainerRef.current);
          portalContainerRef.current = null;
        }
      };
    }, [usePortal]);
    useEffect(() => {
      if (!onInit || !isOpen || initState.status !== 'idle') return;
      if (appId === undefined || portalId === undefined) return;
      let isMounted = true;
      const runInit = async () => {
        try {
          setInitState({
            status: 'loading'
          });
          const resolvedProps = await onInit({
            appId,
            portalId
          });
          if (isMounted) {
            setInitState({
              status: 'ready',
              resolvedProps
            });
          }
        } catch (err) {
          if (isMounted) {
            setInitState({
              status: 'error',
              message: err instanceof Error ? err.message : 'Failed to initialize component'
            });
          }
        }
      };
      void runInit();
      return () => {
        isMounted = false;
      };
    }, [isOpen, appId, portalId, initState.status]);
    const openModal = useCallback(() => {
      if (onInit) {
        setInitState({
          status: 'idle'
        });
      }
      setIsOpen(true);
    }, []);
    const closeModal = useCallback(() => setIsOpen(false), []);
    const handleComplete = useCallback(newAccountId => {
      const hook = isReconnect ? onReconnectComplete : onConnectComplete;
      if (hook && newAccountId !== undefined && appId !== undefined && portalId !== undefined) {
        void (async () => {
          let onCompleteSucceeded = false;
          try {
            await hook(newAccountId, {
              appId,
              portalId
            });
            onComplete({
              accountId: newAccountId
            });
            onCompleteSucceeded = true;
          } catch (err) {
            const shouldCleanup = shouldDeleteOnFailure ? shouldDeleteOnFailure(newAccountId) : true;
            if (shouldCleanup) {
              var _apiHandlers$deleteAc3;
              const deleteFn = (_apiHandlers$deleteAc3 = apiHandlers === null || apiHandlers === void 0 ? void 0 : apiHandlers.deleteAccount) !== null && _apiHandlers$deleteAc3 !== void 0 ? _apiHandlers$deleteAc3 : deleteAccountApi;
              try {
                await deleteFn(newAccountId);
              } catch (cleanupError) {
                console.error('Failed to clean up account after hook error:', cleanupError);
              }
            }
            throw err;
          } finally {
            if (onCompleteSucceeded && !successScreen) {
              closeModal();
            }
          }
        })();
      } else {
        let onCompleteSucceeded = false;
        try {
          onComplete({
            accountId: newAccountId
          });
          onCompleteSucceeded = true;
        } catch (err) {
          if (newAccountId !== undefined) {
            const shouldCleanup = shouldDeleteOnFailure ? shouldDeleteOnFailure(newAccountId) : true;
            if (shouldCleanup) {
              var _apiHandlers$deleteAc4;
              const deleteFn = (_apiHandlers$deleteAc4 = apiHandlers === null || apiHandlers === void 0 ? void 0 : apiHandlers.deleteAccount) !== null && _apiHandlers$deleteAc4 !== void 0 ? _apiHandlers$deleteAc4 : deleteAccountApi;
              void deleteFn(newAccountId);
            }
          }
          throw err;
        } finally {
          if (onCompleteSucceeded && !successScreen) {
            closeModal();
          }
        }
      }
    }, [onComplete, shouldDeleteOnFailure, successScreen, closeModal, appId, portalId, isReconnect]);
    const handleCancel = useCallback(() => {
      closeModal();
      onCancel === null || onCancel === void 0 || onCancel();
    }, [closeModal, onCancel]);
    const renderModalContent = () => {
      if (appId === undefined || portalId === undefined) {
        return /*#__PURE__*/_jsx(Alert, {
          type: "danger",
          titleText: "Configuration Error",
          children: /*#__PURE__*/_jsx(View, {
            paddingBlock: "200",
            paddingInline: "200",
            children: /*#__PURE__*/_jsx(Text, {
              children: "portalId and appId are required"
            })
          })
        });
      }
      if (initState.status === 'loading') {
        return /*#__PURE__*/_jsxs(View, {
          textAlign: "center",
          paddingBlock: "300",
          children: [/*#__PURE__*/_jsx(LoadingSpinner, {}), /*#__PURE__*/_jsx(View, {
            marginBlockStart: "200",
            children: /*#__PURE__*/_jsx(Text, {
              children: "Loading..."
            })
          })]
        });
      }
      if (initState.status === 'error') {
        return /*#__PURE__*/_jsx(Alert, {
          type: "danger",
          titleText: "Initialization Error",
          children: /*#__PURE__*/_jsx(View, {
            paddingBlock: "200",
            paddingInline: "200",
            children: /*#__PURE__*/_jsx(Text, {
              children: initState.message
            })
          })
        });
      }
      if (initState.status === 'ready') {
        var _ref12, _ref13;
        const resolvedAppName = (_ref12 = appName !== null && appName !== void 0 ? appName : initState.resolvedProps.appName) !== null && _ref12 !== void 0 ? _ref12 : '';
        const resolvedOAuthScopes = (_ref13 = oAuthScopes !== null && oAuthScopes !== void 0 ? oAuthScopes : initState.resolvedProps.oAuthScopes) !== null && _ref13 !== void 0 ? _ref13 : initState.resolvedProps.oAuthScope ? initState.resolvedProps.oAuthScope.split(' ').filter(Boolean) : undefined;
        return /*#__PURE__*/_jsx(CoreUnifiedAccountModal, {
          portalId: portalId,
          appId: appId,
          appName: resolvedAppName,
          oAuthScopes: resolvedOAuthScopes,
          mcpParams: mcpParams,
          accountId: accountId,
          oAuthorMigrationParams: oAuthorMigrationParams,
          onComplete: handleComplete,
          onCancel: handleCancel,
          config: {
            shouldDeleteOnFailure,
            introScreen,
            successScreen
          }
        });
      }
      return null;
    };
    const modalElement = isOpen ? renderModalContent() : null;
    const Modal = usePortal && isOpen && portalContainerRef.current ? /*#__PURE__*/createPortal(modalElement, portalContainerRef.current) : modalElement;
    return {
      openModal,
      closeModal,
      isOpen,
      Modal
    };
  }
  async function wrappedDeleteAccount(accountId) {
    var _apiHandlers$deleteAc5;
    const deleteFn = (_apiHandlers$deleteAc5 = apiHandlers === null || apiHandlers === void 0 ? void 0 : apiHandlers.deleteAccount) !== null && _apiHandlers$deleteAc5 !== void 0 ? _apiHandlers$deleteAc5 : deleteAccountApi;
    const result = await deleteFn(accountId);
    if (onDeleteComplete) {
      try {
        await onDeleteComplete(accountId);
      } catch (error) {
        console.error('onDeleteComplete hook failed:', error);
      }
    }
    return result;
  }
  async function wrappedUninstallApp(appId) {
    var _apiHandlers$uninstal;
    const uninstallFn = (_apiHandlers$uninstal = apiHandlers === null || apiHandlers === void 0 ? void 0 : apiHandlers.uninstallApp) !== null && _apiHandlers$uninstal !== void 0 ? _apiHandlers$uninstal : uninstallAppApi;
    const result = await uninstallFn(appId);
    if (onUninstallComplete) {
      try {
        await onUninstallComplete(appId);
      } catch (error) {
        console.error('onUninstallComplete hook failed:', error);
      }
    }
    return result;
  }
  return {
    Button: UnifiedAccountButtonInternal,
    Modal: UnifiedAccountModalInternal,
    useModal: useUnifiedAccountModal,
    deleteAccount: wrappedDeleteAccount,
    uninstallApp: wrappedUninstallApp
  };
}